import React, { useEffect, useState, useCallback } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Search, Edit2, Trash2, Plus, X, Package, AlertTriangle, TrendingDown, EyeOff, Percent, Boxes } from 'lucide-react';

export default function AdminProducts() {
    const [products, setProducts] = useState([]);
    const [total, setTotal] = useState(0);
    const [inventory, setInventory] = useState(null);
    const [q, setQ] = useState('');
    const [brand, setBrand] = useState('');
    const [category, setCategory] = useState('');
    const [stockFilter, setStockFilter] = useState('');
    const [page, setPage] = useState(1);
    const [editing, setEditing] = useState(null);
    const [creating, setCreating] = useState(false);
    const [bulkOpen, setBulkOpen] = useState(false);
    const [busy, setBusy] = useState(false);

    const loadInventory = () => api.get('/admin/catalog/products/inventory').then(({ data }) => setInventory(data));
    const loadProducts = useCallback(() => {
        const qs = new URLSearchParams({ page, page_size: '24' });
        if (q) qs.set('q', q);
        if (brand) qs.set('brand', brand);
        if (category) qs.set('category', category);
        api.get(`/catalog/products?${qs.toString()}`).then(({ data }) => {
            let items = data.items;
            if (stockFilter === 'oos') items = items.filter((p) => (p.stock || 0) <= 0);
            else if (stockFilter === 'low') items = items.filter((p) => p.stock > 0 && p.stock <= 10);
            else if (stockFilter === 'inactive') items = items.filter((p) => !p.is_active);
            setProducts(items);
            setTotal(data.total);
        });
    }, [page, q, brand, category, stockFilter]);

    useEffect(() => { loadInventory(); loadProducts(); }, [loadProducts]);

    const save = async (id, patch) => {
        setBusy(true);
        try {
            await api.patch(`/admin/catalog/products/${id}`, patch);
            toast.success('Saved');
            setEditing(null);
            loadProducts();
            loadInventory();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
        finally { setBusy(false); }
    };

    const remove = async (id) => {
        if (!window.confirm('Delete this product permanently?')) return;
        setBusy(true);
        try {
            await api.delete(`/admin/catalog/products/${id}`);
            toast.success('Deleted');
            loadProducts();
            loadInventory();
        } catch (e) { toast.error('Delete failed'); }
        finally { setBusy(false); }
    };

    const create = async (data) => {
        setBusy(true);
        try {
            await api.post('/admin/catalog/products', data);
            toast.success('Product created');
            setCreating(false);
            loadProducts();
            loadInventory();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Create failed'); }
        finally { setBusy(false); }
    };

    return (
        <AdminLayout>
            <div className="p-10" data-testid="admin-products-page">
                <div className="flex items-end justify-between mb-10 gap-4 flex-wrap">
                    <div>
                        <div className="overline">Catalog Operations</div>
                        <h1 className="font-serif text-4xl text-inorbit-blue mt-2">Product Manager</h1>
                        <div className="text-sm text-text-secondary mt-1">Full Shopify-grade control over the live catalog.</div>
                    </div>
                    <div className="flex gap-2">
                        <button onClick={() => setBulkOpen(true)} className="btn-outline" data-testid="bulk-ops-btn"><Percent size={14} /> Bulk Ops</button>
                        <button onClick={() => setCreating(true)} className="btn-primary" data-testid="new-product-btn"><Plus size={14} /> New product</button>
                    </div>
                </div>

                {/* Inventory KPIs */}
                {inventory && (
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-10" data-testid="inventory-kpis">
                        <KPI label="Total SKUs" value={inventory.total} icon={Package} />
                        <KPI label="In Stock" value={inventory.in_stock} icon={Boxes} color="text-emerald-700" />
                        <KPI label="Low Stock" value={inventory.low_stock} icon={TrendingDown} color="text-amber-700" alert />
                        <KPI label="Out of Stock" value={inventory.out_of_stock} icon={AlertTriangle} color="text-rose-700" alert />
                        <KPI label="Inactive" value={inventory.inactive} icon={EyeOff} color="text-text-tertiary" />
                    </div>
                )}

                {/* Filters */}
                <div className="bg-white border border-border-subtle p-4 rounded-md mb-6 flex flex-wrap gap-3 items-center">
                    <div className="flex items-center gap-2 bg-cream px-3 py-2 flex-1 min-w-[220px] rounded">
                        <Search size={14} className="text-text-tertiary" />
                        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name or SKU" className="flex-1 outline-none text-sm bg-transparent" data-testid="ap-search" />
                    </div>
                    <input value={brand} onChange={(e) => setBrand(e.target.value)} placeholder="Brand" className="input-box w-44" data-testid="ap-brand" />
                    <input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="Category slug" className="input-box w-48" data-testid="ap-category" />
                    <select value={stockFilter} onChange={(e) => setStockFilter(e.target.value)} className="input-box w-40" data-testid="ap-stock-filter">
                        <option value="">All stock</option>
                        <option value="low">Low (1-10)</option>
                        <option value="oos">Out of stock</option>
                        <option value="inactive">Inactive</option>
                    </select>
                    <div className="text-xs uppercase tracking-widest text-text-tertiary ml-auto">{total.toLocaleString('en-IN')} products</div>
                </div>

                {/* Products table */}
                <div className="bg-white border border-border-subtle rounded-md overflow-hidden">
                    <table className="w-full text-sm" data-testid="products-table">
                        <thead className="bg-cream text-text-secondary text-[11px] uppercase tracking-widest">
                            <tr>
                                <th className="text-left p-4">Product</th>
                                <th className="text-left p-4">SKU/Brand</th>
                                <th className="text-right p-4">Price</th>
                                <th className="text-right p-4">Stock</th>
                                <th className="text-center p-4">Status</th>
                                <th className="p-4"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {products.length === 0 ? (
                                <tr><td colSpan="6" className="p-16 text-center text-text-tertiary">No products match the filters.</td></tr>
                            ) : products.map((p) => (
                                <tr key={p.id} className="border-t border-border-subtle hover:bg-cream/40" data-testid={`row-${p.id}`}>
                                    <td className="p-4 flex items-center gap-3">
                                        <div className="w-10 h-10 bg-cream rounded shrink-0 overflow-hidden">
                                            {p.image && <img src={p.image} alt="" className="w-full h-full object-cover" />}
                                        </div>
                                        <div className="min-w-0">
                                            <div className="text-inorbit-blue truncate max-w-md">{p.name}</div>
                                            <div className="text-xs text-text-tertiary">{p.category_slug} • {p.mall_slug}</div>
                                        </div>
                                    </td>
                                    <td className="p-4 text-xs">
                                        <div className="font-mono text-text-secondary">{p.sku || '—'}</div>
                                        <div className="text-inorbit-orange uppercase tracking-widest text-[10px] mt-1">{p.brand}</div>
                                    </td>
                                    <td className="p-4 text-right">
                                        <div className="font-semibold text-inorbit-blue">₹{Number(p.price).toLocaleString('en-IN')}</div>
                                        {p.mrp > p.price && <div className="text-[10px] text-text-tertiary line-through">₹{Number(p.mrp).toLocaleString('en-IN')}</div>}
                                    </td>
                                    <td className={`p-4 text-right font-mono ${p.stock <= 0 ? 'text-rose-700 font-semibold' : p.stock <= 10 ? 'text-amber-700' : 'text-inorbit-blue'}`}>{p.stock ?? '—'}</td>
                                    <td className="p-4 text-center">
                                        <span className={`pill text-[10px] ${p.is_active ? (p.stock <= 0 ? 'pill-outline' : 'pill-blue') : 'pill-outline'}`}>
                                            {!p.is_active ? 'Inactive' : p.stock <= 0 ? 'OOS' : 'Live'}
                                        </span>
                                    </td>
                                    <td className="p-4 text-right whitespace-nowrap">
                                        <button onClick={() => setEditing(p)} className="text-inorbit-blue hover:text-inorbit-orange mr-3" data-testid={`edit-${p.id}`}><Edit2 size={14} /></button>
                                        <button onClick={() => remove(p.id)} className="text-rose-600 hover:text-rose-800" data-testid={`del-${p.id}`}><Trash2 size={14} /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {total > 24 && (
                    <div className="flex justify-center gap-3 mt-8">
                        <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} className="btn-outline disabled:opacity-40">Prev</button>
                        <span className="text-sm text-text-secondary self-center">Page {page} of {Math.ceil(total / 24)}</span>
                        <button onClick={() => setPage(page + 1)} disabled={page >= Math.ceil(total / 24)} className="btn-outline disabled:opacity-40">Next</button>
                    </div>
                )}
            </div>

            {/* Edit modal */}
            {editing && <ProductEditor product={editing} onClose={() => setEditing(null)} onSave={(patch) => save(editing.id, patch)} busy={busy} />}

            {/* Create modal */}
            {creating && <ProductEditor product={null} onClose={() => setCreating(false)} onSave={create} busy={busy} create />}

            {/* Bulk ops modal */}
            {bulkOpen && <BulkOps onClose={() => { setBulkOpen(false); loadProducts(); loadInventory(); }} />}
        </AdminLayout>
    );
}

function KPI({ label, value, icon: Icon, color, alert }) {
    return (
        <div className={`bg-white p-5 border rounded-md ${alert ? 'border-amber-300' : 'border-border-subtle'}`}>
            <Icon size={16} strokeWidth={1.4} className={color || 'text-text-tertiary'} />
            <div className="font-serif text-2xl text-inorbit-blue mt-2">{Number(value).toLocaleString('en-IN')}</div>
            <div className="text-[10px] uppercase tracking-widest text-text-tertiary mt-1">{label}</div>
        </div>
    );
}

function ProductEditor({ product, onClose, onSave, busy, create }) {
    const [form, setForm] = useState(product || {
        name: '', brand: '', price: 0, mrp: 0, image: '', stock: 100, is_active: true,
        category_slug: '', mall_slug: 'malad',
    });
    const set = (k, v) => setForm({ ...form, [k]: v });
    const submit = (e) => {
        e.preventDefault();
        const patch = { ...form };
        if (!create) {
            // Only changed fields
            Object.keys(patch).forEach((k) => { if (patch[k] === product?.[k]) delete patch[k]; });
        }
        onSave(patch);
    };
    return (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="product-editor">
            <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto rounded-md">
                <div className="p-8 border-b border-border-subtle flex items-start justify-between">
                    <div>
                        <div className="overline">{create ? 'New product' : 'Editing'}</div>
                        <h2 className="font-serif text-3xl text-inorbit-blue mt-1">{create ? 'Add to catalog' : product.name}</h2>
                    </div>
                    <button type="button" onClick={onClose} className="text-text-tertiary hover:text-inorbit-blue" data-testid="close-editor"><X size={22} /></button>
                </div>
                <div className="p-8 space-y-5">
                    <Field label="Name"><input required value={form.name} onChange={(e) => set('name', e.target.value)} className="input-box" data-testid="ed-name" /></Field>
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Brand"><input required value={form.brand} onChange={(e) => set('brand', e.target.value)} className="input-box" data-testid="ed-brand" /></Field>
                        <Field label="Category slug"><input value={form.category_slug || ''} onChange={(e) => set('category_slug', e.target.value)} className="input-box" data-testid="ed-cat" /></Field>
                    </div>
                    <Field label="Image URL"><input required value={form.image || ''} onChange={(e) => set('image', e.target.value)} className="input-box" data-testid="ed-image" /></Field>
                    {form.image && <img src={form.image} alt="" className="h-28 rounded border border-border-subtle object-contain" />}
                    <div className="grid grid-cols-3 gap-4">
                        <Field label="Price (₹)"><input required type="number" step="0.01" value={form.price} onChange={(e) => set('price', parseFloat(e.target.value))} className="input-box" data-testid="ed-price" /></Field>
                        <Field label="MRP (₹)"><input type="number" step="0.01" value={form.mrp} onChange={(e) => set('mrp', parseFloat(e.target.value))} className="input-box" data-testid="ed-mrp" /></Field>
                        <Field label="Stock"><input type="number" value={form.stock || 0} onChange={(e) => set('stock', parseInt(e.target.value))} className="input-box" data-testid="ed-stock" /></Field>
                    </div>
                    <label className="flex items-center gap-2 text-sm text-inorbit-blue">
                        <input type="checkbox" checked={form.is_active !== false} onChange={(e) => set('is_active', e.target.checked)} data-testid="ed-active" />
                        Active (visible to shoppers)
                    </label>
                </div>
                <div className="p-6 border-t border-border-subtle flex justify-end gap-2">
                    <button type="button" onClick={onClose} className="btn-outline" data-testid="ed-cancel">Cancel</button>
                    <button type="submit" disabled={busy} className="btn-primary" data-testid="ed-save">{busy ? 'Saving…' : 'Save'}</button>
                </div>
            </form>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <label className="overline mb-1 block">{label}</label>
            {children}
        </div>
    );
}

function BulkOps({ onClose }) {
    const [brand, setBrand] = useState('');
    const [category, setCategory] = useState('');
    const [discount, setDiscount] = useState(20);
    const [stock, setStock] = useState(50);
    const [busy, setBusy] = useState(false);

    const applyDiscount = async () => {
        if (!brand && !category) return toast.error('Provide brand or category');
        setBusy(true);
        try {
            const { data } = await api.post('/admin/catalog/products/bulk-discount', { brand, category_slug: category, discount_pct: parseFloat(discount) });
            toast.success(`${data.updated} products discounted ${discount}%`);
        } catch (e) { toast.error('Failed'); }
        finally { setBusy(false); }
    };
    const applyStock = async () => {
        if (!brand && !category) return toast.error('Provide brand or category');
        setBusy(true);
        try {
            const { data } = await api.post('/admin/catalog/products/bulk-stock', { brand, category_slug: category, stock: parseInt(stock) });
            toast.success(`Stock set on ${data.updated} products`);
        } catch (e) { toast.error('Failed'); }
        finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="bulk-ops-modal">
            <div onClick={(e) => e.stopPropagation()} className="bg-white max-w-xl w-full rounded-md max-h-[90vh] overflow-y-auto">
                <div className="p-8 border-b border-border-subtle flex justify-between">
                    <div><div className="overline">Bulk Operations</div><h2 className="font-serif text-3xl text-inorbit-blue mt-1">Apply at scale</h2></div>
                    <button onClick={onClose} className="text-text-tertiary hover:text-inorbit-blue"><X size={20} /></button>
                </div>
                <div className="p-8 space-y-5">
                    <Field label="Filter — Brand"><input value={brand} onChange={(e) => setBrand(e.target.value)} className="input-box" placeholder="Exactly as in catalog" data-testid="bulk-brand" /></Field>
                    <Field label="Filter — Category slug"><input value={category} onChange={(e) => setCategory(e.target.value)} className="input-box" placeholder="e.g. women-western-wear" data-testid="bulk-cat" /></Field>
                </div>
                <div className="px-8 pb-8 border-b border-border-subtle">
                    <div className="overline mb-3">Action: discount across filter</div>
                    <div className="flex items-end gap-2">
                        <Field label="% off MRP"><input type="number" min="0" max="80" value={discount} onChange={(e) => setDiscount(e.target.value)} className="input-box w-28" data-testid="bulk-discount" /></Field>
                        <button onClick={applyDiscount} disabled={busy} className="btn-primary" data-testid="bulk-discount-apply"><Percent size={14} /> Apply discount</button>
                    </div>
                </div>
                <div className="px-8 py-8">
                    <div className="overline mb-3">Action: bulk set stock</div>
                    <div className="flex items-end gap-2">
                        <Field label="New stock value"><input type="number" min="0" value={stock} onChange={(e) => setStock(e.target.value)} className="input-box w-28" data-testid="bulk-stock-val" /></Field>
                        <button onClick={applyStock} disabled={busy} className="btn-primary" data-testid="bulk-stock-apply"><Boxes size={14} /> Apply stock</button>
                    </div>
                </div>
            </div>
        </div>
    );
}
